import moment from 'moment';
import { type EventHistogramRange } from 'reducers/histogramReducer';
import * as constants from 'vd-constants';

interface LocaleGeneralTimeStrings {
  yrs: string;
  mos: string;
  days: string;
  hrs: string;
  mins: string;
  secs: string;
  secsShort: string;
  minsShort: string;
  hrsShort: string;
}

const DAY_MS = 24 * 60 * 60 * 1000;
const MINUTE_MS = 60 * 1000;

export const FORMAT_UTC = 'YYYY-MM-DDTHH:mm:ss.SSSZ';

/**
 * Get new date instance.
 * @param {string|number|Date} [date=now] - Date to convert. Defaults to current date.
 * @returns {Date}
 */
export function getNewDate(date?: string | number | Date): Date {
  return date ? new Date(date) : new Date();
}

/**
 * Get Unix timestamp (in milliseconds).
 * @param {string|number|Date} [date=now] - Date to convert. Defaults to current date.
 * @returns {number} Returns a new Unix timestamp.
 */
export function getTimestamp(date?: string | number | Date): number {
  return date ? +new Date(date) : Date.now();
}

/**
 * Get the difference between the provided dates (in milliseconds).
 * @param {string|number|Date} date1
 * @param {string|number|Date} [date2] - Defaults to current date.
 * @returns {number} The difference in milliseconds (return value will be positive if date1 > date2 and negative if (date1 < date2).
 */
export function getDiff(date1: string | number | Date, date2: string | number | Date) {
  const timestamp1 = getTimestamp(date1);
  const timestamp2 = getTimestamp(date2);

  return timestamp1 - timestamp2;
}

/**
 * Get the difference between the provided dates (in milliseconds).
 * @param {string|number|Date} date1
 * @param {string|number|Date} [date2] - Defaults to current date.
 * @returns {number} The difference in milliseconds (returns a positive value regardless whether date1 is greater or lesser than date2).
 */
export function getAbsDiff(date1: string | number | Date, date2: string | number | Date) {
  return Math.abs(getDiff(date1, date2));
}

/**
 * Check if the provided dates are the same.
 * @param {string|number|Date} date1
 * @param {string|number|Date} date2
 * @returns {boolean} Returns true if the dates are the same, false otherwise.
 */
export function isSame(date1: string | number | Date, date2: string | number | Date) {
  return getTimestamp(date1) === getTimestamp(date2);
}

/**
 * Check if the provided dates are the same up to the day (i.e. ignores time).
 * @param {string|number|Date} date1
 * @param {string|number|Date} date2
 * @returns {boolean} Returns true if the dates are the same up to the day, false otherwise.
 */
export function isSameDay(date1: string | number | Date, date2: string | number | Date) {
  const day1 = getNewDate(date1);
  day1.setHours(0, 0, 0, 0);
  const day2 = getNewDate(date2);
  day2.setHours(0, 0, 0, 0);
  return getTimestamp(day1) === getTimestamp(day2);
}

/**
 * Check if the provided dates are the same up to the year (i.e. ignores month, day and time).
 * @param {string|number|Date} date1
 * @param {string|number|Date} date2
 * @returns {boolean} Returns true if the dates have the same year, false otherwise.
 */
export function isSameYear(date1: string | number | Date, date2: string | number | Date) {
  const year1 = getNewDate(date1).getFullYear();
  const year2 = getNewDate(date2).getFullYear();
  return year1 === year2;
}

/**
 * Check if a date is before another date.
 * @param {string|number|Date} date1
 * @param {string|number|Date} date2
 * @returns {boolean} Returns true if the first date (date1) is before the second date (date2), false otherwise.
 */
export function isBefore(date1: string | number | Date, date2: string | number | Date) {
  return getTimestamp(date1) < getTimestamp(date2);
}

/**
 * Add specified minutes to provided date.
 * @param {number} minutes
 * @param {string|number|Date} [date=now] - Add minutes to this date. Defaults to current date.
 * @returns {Date} Returns a new date with the added minutes.
 */
export function addMinutesToDate(minutes: number, date: string | number | Date) {
  return getNewDate(getTimestamp(date) + minutes * MINUTE_MS);
}

/**
 * Subtract specified minutes from provided date.
 * @param {number} minutes
 * @param {string|number|Date} [date=now] - Subtract minutes from this date. Defaults to current date.
 * @returns {Date} Returns a new date with the subtracted minutes.
 */
export function subtractMinutesFromDate(minutes: number, date: string | number | Date) {
  return getNewDate(getTimestamp(date) - minutes * MINUTE_MS);
}

/**
 * Add number of days to provided date.
 * @param {number} days
 * @param {string|number|Date} [date=now] - Add days to this date. Defaults to current date.
 * @returns {Date} Returns a new date with the added days.
 */
export function addDaysToDate(days: number, date: string | number | Date) {
  return getNewDate(getTimestamp(date) + days * DAY_MS);
}

/**
 * Substract number of days from provided date.
 * @param {number} days
 * @param {string|number|Date} [date=now] - Subtract days from this date. Defaults to current date.
 * @returns {Date} Returns a new date with the subtracted days.
 */
export function subtractDaysFromDate({
  days,
  date,
}: {
  days: number;
  date: string | number | Date;
}) {
  return getNewDate(getTimestamp(date) - days * DAY_MS);
}

/**
 * Convert value to timestamp (in milliseconds).
 * @param {number} value
 * @param {string} format
 */
export function toMilliseconds(value: number, format: string = constants.SECONDS) {
  let msValue;

  switch (format) {
    case constants.SECONDS: {
      msValue = value * 1000;
      break;
    }
    default: {
      throw new Error(`Invalid time format provided "${format}"`);
    }
  }

  return msValue;
}

/**
 * TODO: isn't this same with new Date(date).toISOString() ?
 * Format provided date to a UTC date string.
 * @param {string|number|Date} [date=now] - Date to format. Defaults to current date.
 * @returns {string} Returns the formatted string.
 */
export function toUtcString(date: string | number | Date): string {
  return moment(date).format(FORMAT_UTC);
}

/**
 * Format provided date to a time for display.
 * @param {string|number|Date} [date=now] - Date to format. Defaults to current date.
 * @returns {string} Returns the formatted string.
 */
export function toTimeString(date: string | number | Date): string {
  return moment(date).format('h:mmA');
}

/**
 * Format provided date to a compact string for display (responsive design).
 * @param {string|number|Date} - Date to format. Defaults to current date.
 * @returns {{time: string, date: string}} Returns the formatted string. Time on same day; day and month in same year; otherwise day, month and year.
 */
export function toCompactString(date: string | number | Date): { time: string; date: string } {
  // Returning in time/date format for localization (e.g. "last at 11:00AM" vs "last on 20 June")
  const compact = {
    time: '',
    date: '',
  };

  if (isSameDay(date, Date.now())) {
    compact.time = toTimeString(date);
  } else {
    compact.date = moment(date).format('D MMM, YYYY');
  }

  return compact;
}

/**
 * Format the provided date to be used in a filename.
 * @param {string|number|Date} [date=now] - Date to format. Defaults to current date.
 * @returns {string} Returns the formatted string.
 */
export function toFilenameString(date?: string | number | Date): string {
  const momentDate = moment(date);
  const day = momentDate.format('ddd MMM DD YYYY');
  const hour = momentDate.format('HH');
  const min = momentDate.format('mm');
  const sec = momentDate.format('ss');
  const zone = momentDate.format('Z');

  return `${day} ${hour}h ${min}m ${sec}s (GMT${zone})`.replace(':', '-');
}

/**
 * Get duration in the 'x hrs, x mins, x secs' format
 * @param locale - translations.
 * @param start - start time
 * @param end - end time
 * @param [options.showSeconds=true] - Show seconds.
 * @returns Returns the formatted string
 */
export function getDuration(
  locale: {
    general: LocaleGeneralTimeStrings;
  },
  start: string | number | Date,
  end: string | number | Date,
  options: { showSeconds?: boolean } = {},
): string {
  return durationToString(locale, getTimestamp(end) - getTimestamp(start), options);
}

export function durationToString(
  locale: {
    general: LocaleGeneralTimeStrings;
  },
  durationInMs: number,
  // add a option to show shortened format like 1m 1s
  options: { showSeconds?: boolean; showShortened?: boolean } = {},
): string {
  const showSeconds = options.showSeconds ?? true;
  const showShortened = options.showShortened ?? false;
  if (Math.abs(durationInMs) < 1000) {
    if (showSeconds) {
      return `0 ${showShortened ? locale.general.secsShort : locale.general.secs}`;
    }
    return `0 ${showShortened ? locale.general.minsShort : locale.general.mins}`;
  }

  const duration = moment.duration(durationInMs);

  const parts = [
    duration.years() ? `${duration.years()} ${locale.general.yrs}` : '',
    duration.months() ? `${duration.months()} ${locale.general.mos}` : '',
    duration.days() ? `${duration.days()} ${locale.general.days}` : '',
    duration.hours()
      ? `${duration.hours()} ${showShortened ? locale.general.hrsShort : locale.general.hrs}`
      : '',
    duration.minutes()
      ? `${duration.minutes()} ${showShortened ? locale.general.minsShort : locale.general.mins}`
      : '',
    duration.seconds() && showSeconds
      ? `${duration.seconds()} ${showShortened ? locale.general.secsShort : locale.general.secs}`
      : '',
  ];

  return parts.filter(Boolean).join(', ');
}

export function getRollingRangeFromDuration(
  start: number | Date,
  end: number | Date,
): EventHistogramRange {
  const duration = moment.duration(moment(end).diff(start));

  if (duration.years() || duration.months()) {
    return constants.RANGE_YEAR;
  }

  // add one day so the start day is included
  const days = moment(end).endOf('day').diff(moment(start).startOf('day'), 'days') + 1;

  if (days) {
    // TODO: define constant RANGE_MONTH_DAY_COUNT in vd-common
    if (days > 28) {
      return constants.RANGE_YEAR;
    }

    if (days > 7) {
      return constants.RANGE_MONTH;
    }

    if (days === 1) {
      return constants.RANGE_DAY;
    }

    return constants.RANGE_WEEK;
  }

  return constants.RANGE_DAY;
}

/** get the number of months since year 0 */
function getAbsoluteMonths(date: number) {
  const momentDate = moment(date);

  const months = Number(momentDate.format('MM'));
  const years = Number(momentDate.format('YYYY'));

  return months + years * 12;
}

export function getAbsoluteMonthsDifference(end: number, start: number) {
  return getAbsoluteMonths(end) - getAbsoluteMonths(start);
}
